import './Poll.sass';

import { FC, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { PollProps } from './Poll.interface';
import { classNames } from '$utils';
import { toastActions } from '$store/toast';
import { Api } from '$api';
import { usePost, useTranslator } from '$hooks';
import { postsActions } from '$store/posts';
import { IcVerified16 } from '$assets';
import { authActions, authIsLoggedSelector } from '$store/auth';
import { mainActions } from '$store/main';
import { profileActions } from '$store/profile';
import { AuthPopoverState } from '$store/models';

export const Poll: FC<PollProps> = (props) => {
  const { poll, postId } = props;
  const dispatch = useDispatch();
  const t = useTranslator();
  const isResult = !!poll.voted || poll.stopped;
  const post = usePost(postId);
  const isLogged = useSelector(authIsLoggedSelector);

  const handleAnswer = useCallback(
    (index: number) => () => {
      if (!isLogged) {
        return dispatch(profileActions.setAuthPopoverState(AuthPopoverState.LOGIN));
      }

      dispatch(toastActions.setToastLoading());
      Api.post('/poll/answer', {
        pollId: poll.pollId,
        options: [index],
        postId: postId,
      })
        .then((resp) => {
          dispatch(toastActions.hideToast());
          const data: any = resp;
          dispatch(
            postsActions.setPost({
              id: postId,
              updated: data.post,
            }),
          );
          if (data.post.adId > 0) {
            dispatch(
              postsActions.sendAdConversion({
                adId: data.post.adId,
              }),
            );
          }
        })
        .catch((e) => {
          dispatch(toastActions.setToastFail(e.message));
        });
    },
    [post, poll, isLogged],
  );

  const options = useMemo(() => {
    return poll.options.map((option, index) => {
      const percent = Math.round((option.answers / poll.totalVotes) * 100);
      return (
        <div
          className="Poll__option"
          key={index}
          onClick={isResult ? undefined : handleAnswer(index)}
        >
          <div className="Poll__option__cont">
            <div className="Poll__option__before">
              {isResult && (
                <div className="Poll__option__percent">{Math.round(percent)}%</div>
              )}
              {!isResult && <div className="Poll__option__vote_btn" />}
              {poll.voted && poll.voted.indexOf(index) !== -1 && (
                <div className="Poll__option_checked">
                  <IcVerified16 />
                </div>
              )}
            </div>
            <div className="Poll__option__label">{option.label}</div>
          </div>
          <div className="Poll__option__progress">
            {isResult && (
              <div
                className="Poll__option__progress__indicator"
                style={{ width: `${percent}%` }}
              />
            )}
          </div>
        </div>
      );
    });
  }, [isResult, poll]);

  return (
    <div
      className={classNames({
        Poll: true,
        result: isResult,
      })}
    >
      <div className="Poll__title">{poll.title}</div>
      <div className="Poll__label">
        {t(poll.anonymous ? 'poll_type_anonymous' : 'poll_type_public')}
      </div>
      <div className="Poll__options">{options}</div>
      <div className="Poll__total_votes">
        {poll.totalVotes > 0
          ? t('poll_total_votes', { count: poll.totalVotes })
          : t('poll_total_votes_none')}
      </div>
    </div>
  );
};
